import React from 'react';
import { canAccessQCModule } from '../../utils/roleConstants';
import { Form, FormGroup, FormControl, Button, Row, Col, Table } from 'react-bootstrap';
import config from '../../../config';
import { Link, Navigate, useParams, useNavigate } from 'react-router-dom';
import CarReportPrintView from './CarReportPrintView';
import { printCarReport } from './carReportPrint';
import QValuePageHeader from '../../_components/QValuePageHeader';
import MasterPartNoInput from '../../_components/MasterPartNoInput';
import UploadFiles from '../../_components/upload-files.component';
import UploadService from '../../_services/upload-files.service';
import { getQValueReportTrackerFormHeader } from '../../utils/qValuePageHeaders';
import { getQValueAccountId } from './qValueAccount';
import {
    CAR_ORIGIN_OPTIONS,
    CAR_ROW_STATUS_OPTIONS,
    CAR_EFFECTIVENESS_METHODS,
    DEFAULT_ROOT_CAUSE,
    DEFAULT_EFFECTIVENESS,
    emptyContainmentRow,
    emptyCorrectivePlanRow,
    computeCarDisplayStatus,
    formatDateOnly,
    parseJsonArray,
    parseJsonObject,
    parseReportMediaArray,
    mapUploadedImageFiles,
} from './carReportUtils';
import { ReportPhotoPreviewList } from './qValueReportMedia';
import QValueReportApprovalBar from './QValueReportApprovalBar';
import {
    canSaveQValueReportDraft,
    isQValueReportReadOnly,
    Q_VALUE_REPORT_RECORD_TYPES,
    shouldShowSubmitForApproval,
} from './qValueReportFormLock';
import { apiFetch } from '../../_services/apiFetch';

const sectionTitleStyle = {
    color: '#1e3a5f',
    fontWeight: 700,
    fontSize: '15px',
    marginTop: '28px',
    marginBottom: '12px',
    textTransform: 'uppercase',
};

const tableHeadStyle = {
    backgroundColor: '#1e3a5f',
    color: '#fff',
};

function normalizePlanRows(items) {
    const rows = parseJsonArray(items);
    if (!rows.length) return [emptyCorrectivePlanRow()];
    return rows.map((row) => ({
        actionDescription: row.actionDescription || row.action || '',
        systemDocumentUpdated: row.systemDocumentUpdated || '',
        owner: row.owner || '',
        targetDate: formatDateOnly(row.targetDate || row.actionDate),
        completionDate: formatDateOnly(row.completionDate),
    }));
}

function normalizeContainmentRows(items) {
    const rows = parseJsonArray(items);
    if (!rows.length) return [emptyContainmentRow()];
    return rows.map((row) => ({
        actionTaken: row.actionTaken || '',
        responsiblePerson: row.responsiblePerson || '',
        dateCompleted: formatDateOnly(row.dateCompleted),
        status: row.status || 'Open',
    }));
}

const emptyState = () => ({
    id: null,
    carNo: '',
    status: 'Open',
    originSource: '',
    dateOpened: '',
    targetDate: '',
    partNo: '',
    partDescription: '',
    jobLotBatch: '',
    productionLineArea: '',
    ownerName: '',
    problemDescription: '',
    problemPhotos: [],
    containmentActions: [emptyContainmentRow()],
    rootCauseAnalysis: { ...DEFAULT_ROOT_CAUSE },
    correctiveActionPlan: [emptyCorrectivePlanRow()],
    effectivenessVerification: { ...DEFAULT_EFFECTIVENESS },
    dateClosed: '',
    saving: false,
    photoMessage: '',
    evidencePhotoMessage: '',
    isClosedLocked: false,
    approveStatus: 0,
    approvalLabel: '',
    rejectionReason: '',
});

class CorrectiveActionReportForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = emptyState();
        this.printRef = React.createRef();
    }

    componentDidMount() {
        this.syncRoute();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.params && this.props.params && prevProps.params.id !== this.props.params.id) {
            this.syncRoute();
        }
    }

    getRouteId() {
        const fromParams = this.props.params && this.props.params.id;
        if (fromParams) return fromParams;
        const hash = window.location.hash || '';
        const match = hash.match(/\/corrective_action_report\/([^/?]+)/);
        return match ? match[1] : null;
    }

    syncRoute() {
        const id = this.getRouteId();
        if (id && id !== 'new') {
            this.loadRecord(id);
        } else {
            const today = new Date().toISOString().slice(0, 10);
            this.setState(Object.assign(emptyState(), { dateOpened: today }), () => {
                this.loadNextCarNo();
            });
        }
    }

    loadNextCarNo() {
        const company = getQValueAccountId();
        fetch(`${config.apiUrl}/getNextCorrectiveActionReportCarNo?company=${company}`)
            .then((r) => r.json())
            .then((data) => {
                if (data && data.carNo && !this.state.id) {
                    this.setState({ carNo: data.carNo });
                }
            })
            .catch(() => {});
    }

    loadRecord(id) {
        const company = getQValueAccountId();
        fetch(`${config.apiUrl}/getCorrectiveActionReportById?id=${id}&company=${company}`)
            .then((r) => r.json())
            .then((data) => {
                if (!data || !data.id) return;
                this.setState({
                    id: data.id,
                    carNo: data.carNo || '',
                    status: data.status || 'Open',
                    originSource: data.originSource || '',
                    dateOpened: formatDateOnly(data.dateOpened),
                    targetDate: formatDateOnly(data.targetDate || data.dueDate),
                    partNo: data.partNo || '',
                    partDescription: data.partDescription || '',
                    jobLotBatch: data.jobLotBatch || '',
                    productionLineArea: data.productionLineArea || '',
                    ownerName: data.ownerName || '',
                    problemDescription: data.problemDescription || '',
                    problemPhotos: parseReportMediaArray(data.problemPhotos),
                    containmentActions: normalizeContainmentRows(data.containmentActions),
                    rootCauseAnalysis: parseJsonObject(data.rootCauseAnalysis, DEFAULT_ROOT_CAUSE),
                    correctiveActionPlan: normalizePlanRows(data.actionItems),
                    effectivenessVerification: (() => {
                        const eff = parseJsonObject(data.effectivenessVerification, DEFAULT_EFFECTIVENESS);
                        eff.evidencePhotos = parseReportMediaArray(eff.evidencePhotos);
                        return eff;
                    })(),
                    dateClosed: formatDateOnly(data.dateClosed),
                    approveStatus: Number(data.approveStatus || data.approve_status || 0),
                    approvalLabel: data.approvalLabel || '',
                    rejectionReason: data.rejectionReason || data.rejection_reason || '',
                    isClosedLocked: !!data.isReadOnly,
                });
            });
    }

    buildSavePayload() {
        const companyId = getQValueAccountId();
        return {
            id: this.state.id,
            companyId,
            carNo: this.state.carNo,
            originSource: this.state.originSource,
            dateOpened: this.state.dateOpened || null,
            dueDate: this.resolveDueDate(),
            partNo: this.state.partNo,
            partDescription: this.state.partDescription,
            jobLotBatch: this.state.jobLotBatch,
            productionLineArea: this.state.productionLineArea,
            problemDescription: this.state.problemDescription,
            ownerName: this.state.ownerName,
            dateClosed: this.state.dateClosed || null,
            status: this.state.status,
            problemPhotos: this.state.problemPhotos,
            containmentActions: this.state.containmentActions,
            rootCauseAnalysis: this.state.rootCauseAnalysis,
            actionItems: this.state.correctiveActionPlan,
            effectivenessVerification: this.state.effectivenessVerification,
            title: this.state.problemDescription ? this.state.problemDescription.slice(0, 255) : null,
        };
    }

    handleStatusChange = (status) => {
        const today = new Date().toISOString().slice(0, 10);
        if (status === 'Closed') {
            this.setState({ status, dateClosed: this.state.dateClosed || today });
            return;
        }
        this.setState({ status, dateClosed: '' });
    };

    updateContainmentRow = (index, field, value) => {
        const containmentActions = this.state.containmentActions.map((row, i) => (
            i === index ? { ...row, [field]: value } : row
        ));
        this.setState({ containmentActions });
    };

    addContainmentRow = () => {
        this.setState({ containmentActions: [...this.state.containmentActions, emptyContainmentRow()] });
    };

    removeContainmentRow = (index) => {
        const rows = this.state.containmentActions.filter((_, i) => i !== index);
        this.setState({ containmentActions: rows.length ? rows : [emptyContainmentRow()] });
    };

    updatePlanRow = (index, field, value) => {
        const correctiveActionPlan = this.state.correctiveActionPlan.map((row, i) => (
            i === index ? { ...row, [field]: value } : row
        ));
        this.setState({ correctiveActionPlan });
    };

    addPlanRow = () => {
        this.setState({ correctiveActionPlan: [...this.state.correctiveActionPlan, emptyCorrectivePlanRow()] });
    };

    removePlanRow = (index) => {
        const rows = this.state.correctiveActionPlan.filter((_, i) => i !== index);
        this.setState({ correctiveActionPlan: rows.length ? rows : [emptyCorrectivePlanRow()] });
    };

    updateRootCause = (field, value) => {
        this.setState({
            rootCauseAnalysis: { ...this.state.rootCauseAnalysis, [field]: value },
        });
    };

    updateEffectiveness = (field, value) => {
        this.setState({
            effectivenessVerification: { ...this.state.effectivenessVerification, [field]: value },
        });
    };

    toggleEffectivenessMethod = (method) => {
        const current = this.state.effectivenessVerification.verificationMethods || [];
        const next = current.includes(method)
            ? current.filter((m) => m !== method)
            : [...current, method];
        this.updateEffectiveness('verificationMethods', next);
    };

    uploadProblemPhoto = (event) => {
        const files = event.target.files;
        if (!files || !files.length) return;
        UploadService.uploadImage(files)
            .then((response) => {
                const uploaded = mapUploadedImageFiles(response.data);
                this.setState({
                    problemPhotos: [...this.state.problemPhotos, ...uploaded],
                    photoMessage: 'Photo uploaded.',
                });
            })
            .catch(() => this.setState({ photoMessage: 'Could not upload photo.' }));
    };

    removePhoto = (index) => {
        this.setState({
            problemPhotos: this.state.problemPhotos.filter((_, i) => i !== index),
        });
    };

    uploadEvidencePhoto = (event) => {
        const files = event.target.files;
        if (!files || !files.length) return;
        UploadService.uploadImage(files)
            .then((response) => {
                const uploaded = mapUploadedImageFiles(response.data);
                const eff = this.state.effectivenessVerification;
                this.setState({
                    effectivenessVerification: {
                        ...eff,
                        evidencePhotos: [...parseReportMediaArray(eff.evidencePhotos), ...uploaded],
                    },
                    evidencePhotoMessage: 'Evidence photo uploaded.',
                });
            })
            .catch(() => this.setState({ evidencePhotoMessage: 'Could not upload evidence photo.' }));
    };

    removeEvidencePhoto = (index) => {
        const eff = this.state.effectivenessVerification;
        const photos = parseReportMediaArray(eff.evidencePhotos);
        this.setState({
            effectivenessVerification: {
                ...eff,
                evidencePhotos: photos.filter((_, i) => i !== index),
            },
        });
    };

    resolveDueDate() {
        const manual = formatDateOnly(this.state.targetDate);
        if (manual) return manual;
        let latest = '';
        this.state.correctiveActionPlan.forEach((row) => {
            const d = formatDateOnly(row.targetDate);
            if (d && (!latest || d > latest)) latest = d;
        });
        return latest || null;
    }

    handleSave = () => {
        if (!canSaveQValueReportDraft(this.state)) {
            window.alert('This CAR is closed and read-only.');
            return;
        }
        this.setState({ saving: true });
        const payload = this.buildSavePayload();
        fetch(`${config.apiUrl}/saveCorrectiveActionReport`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then((r) => r.json())
            .then((row) => {
                this.setState({ saving: false });
                if (!row || row.error || !row.id) {
                    window.alert((row && row.error) || 'Save failed.');
                    return;
                }
                window.alert('Saved.');
                if (this.props.navigate) {
                    this.props.navigate('/corrective_action_report');
                }
            })
            .catch(() => this.setState({ saving: false }));
    };

    handleSubmitForApproval = () => {
        if (!shouldShowSubmitForApproval(this.state)) return;
        this.setState({ saving: true });
        const payload = this.buildSavePayload();
        apiFetch(`${config.apiUrl}/submitCorrectiveActionReportForApproval`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then((r) => r.json())
            .then((body) => {
                this.setState({ saving: false });
                if (!body || body.error) {
                    window.alert(body && body.error ? body.error : 'Submit for approval failed.');
                    return;
                }
                window.alert(body.message || 'Submitted for approval.');
                if (body.data && body.data.id) {
                    this.loadRecord(body.data.id);
                }
            })
            .catch(() => {
                this.setState({ saving: false });
                window.alert('Submit for approval failed.');
            });
    };

    renderSectionTitle(text) {
        return <div style={sectionTitleStyle}>{text}</div>;
    }

    getPrintData() {
        const targetDate = this.state.targetDate || this.resolveDueDate();
        const dateClosed = this.state.dateClosed;
        const status = this.state.status;
        return {
            carNo: this.state.carNo,
            originSource: this.state.originSource,
            dateOpened: this.state.dateOpened,
            targetDate,
            partNo: this.state.partNo,
            partDescription: this.state.partDescription,
            jobLotBatch: this.state.jobLotBatch,
            productionLineArea: this.state.productionLineArea,
            ownerName: this.state.ownerName,
            problemDescription: this.state.problemDescription,
            problemPhotos: this.state.problemPhotos,
            containmentActions: this.state.containmentActions,
            rootCauseAnalysis: this.state.rootCauseAnalysis,
            correctiveActionPlan: this.state.correctiveActionPlan,
            effectivenessVerification: this.state.effectivenessVerification,
            status,
            dateClosed,
            displayStatus: computeCarDisplayStatus(status, dateClosed, targetDate),
        };
    }

    render() {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!canAccessQCModule(user)) {
            return <Navigate to="/" />;
        }
        const id = this.getRouteId();
        const isNew = !id || id === 'new';
        const company = getQValueAccountId();
        const readOnly = isQValueReportReadOnly(this.state);
        const showSubmitApproval = shouldShowSubmitForApproval(this.state);
        const showSave = canSaveQValueReportDraft(this.state) && (isNew || this.state.status !== 'Closed');
        const {
            carNo, status, originSource, dateOpened, targetDate, partNo, partDescription,
            jobLotBatch, productionLineArea, ownerName, problemDescription, problemPhotos,
            containmentActions, rootCauseAnalysis, correctiveActionPlan, effectivenessVerification,
            dateClosed, saving, photoMessage, approvalLabel, rejectionReason,
        } = this.state;

        return (
            <div className="container">
                <QValuePageHeader
                    {...getQValueReportTrackerFormHeader('corrective_action_report', { isNew })}
                />
                {readOnly && (
                    <QValueReportApprovalBar
                        recordType={Q_VALUE_REPORT_RECORD_TYPES.CAR}
                        recordId={this.state.id}
                        approvalLabel={approvalLabel}
                        rejectionReason={rejectionReason}
                        onDecisionComplete={() => this.loadRecord(this.state.id)}
                    />
                )}
                {readOnly && (
                    <p className="text-muted small mb-3">This CAR is closed and read-only. Use Print to review the report.</p>
                )}
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <Link to="/corrective_action_report">← Back to CAR summary</Link>
                    <div className="ms-auto d-flex gap-2">
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={() => printCarReport(
                                this.printRef.current,
                                carNo || 'CAR-Report'
                            )}
                        >
                            Print
                        </Button>
                    </div>
                </div>
                <Form>
                    <fieldset disabled={readOnly} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                    {this.renderSectionTitle('1. General Information')}
                    <FormGroup as={Row} className="mb-3 align-items-center flex-md-nowrap">
                        <Col md={3} xs={4} className="col-form-label">
                            <Form.Label className="mb-0">CAR Number</Form.Label>
                        </Col>
                        <Col md={9} xs={8}>
                            <FormControl
                                className="qvalue-document-no-field"
                                value={carNo}
                                readOnly
                                placeholder={isNew && !carNo ? 'Loading next CAR number…' : 'e.g. CAR-2026-001'}
                            />
                        </Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Date Opened</Form.Label></Col>
                        <Col md={9}><FormControl type="date" value={dateOpened} onChange={(e) => this.setState({ dateOpened: e.target.value })} /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Origin / Source</Form.Label></Col>
                        <Col md={9}>
                            <FormControl as="select" value={originSource} onChange={(e) => this.setState({ originSource: e.target.value })}>
                                <option value="">— Select —</option>
                                {CAR_ORIGIN_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </FormControl>
                        </Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Part Number</Form.Label></Col>
                        <Col md={9}>
                            <MasterPartNoInput
                                value={partNo}
                                onChange={(v) => this.setState({ partNo: v })}
                                company={company}
                                onPartDataMatch={(part) => {
                                    if (part && part.description) {
                                        this.setState({ partDescription: part.description });
                                    }
                                }}
                            />
                        </Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Part Description</Form.Label></Col>
                        <Col md={9}>
                            <FormControl value={partDescription} readOnly placeholder="Auto from BOM / part master when part number is valid" />
                        </Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Job / Lot / Batch Number</Form.Label></Col>
                        <Col md={9}><FormControl value={jobLotBatch} onChange={(e) => this.setState({ jobLotBatch: e.target.value })} /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Production Line / Area</Form.Label></Col>
                        <Col md={9}><FormControl value={productionLineArea} onChange={(e) => this.setState({ productionLineArea: e.target.value })} placeholder="Workstation, line, or machine" /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Assigned Owner</Form.Label></Col>
                        <Col md={9}><FormControl value={ownerName} onChange={(e) => this.setState({ ownerName: e.target.value })} /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Summary Target Date</Form.Label></Col>
                        <Col md={9}>
                            <FormControl type="date" value={targetDate} onChange={(e) => this.setState({ targetDate: e.target.value })} />
                            <Form.Text className="text-muted">Used on the CAR summary tracker. If blank, the latest plan target date (section 5) is used.</Form.Text>
                        </Col>
                    </FormGroup>

                    {this.renderSectionTitle('2. Problem Description (The Nonconformity)')}
                    <p className="text-muted small mb-2">
                        Provide a detailed explanation of the defect, discrepancy, or process failure. Include specific data (e.g., measured dimensions vs. blueprint limits, quantities affected).
                    </p>
                    <FormGroup className="mb-3">
                        <FormControl as="textarea" rows={4} value={problemDescription} onChange={(e) => this.setState({ problemDescription: e.target.value })} />
                    </FormGroup>
                    <FormGroup className="mb-3">
                        <Form.Label>Photos</Form.Label>
                        <UploadFiles onUpload={this.uploadProblemPhoto} />
                        {photoMessage && <div className="small text-muted mt-1">{photoMessage}</div>}
                        <ReportPhotoPreviewList items={problemPhotos} onRemove={this.removePhoto} />
                    </FormGroup>

                    {this.renderSectionTitle('3. Immediate Containment Actions (Correction)')}
                    <p className="text-muted small mb-2">
                        What short-term actions were taken within 24 hours to isolate the defect, stop further scrap, and protect the customer?
                    </p>
                    <div className="table-responsive mb-2">
                        <Table bordered size="sm" className="mb-0">
                            <thead>
                                <tr style={tableHeadStyle}>
                                    <th>Action Taken</th>
                                    <th style={{ width: '140px' }}>Responsible Person</th>
                                    <th style={{ width: '130px' }}>Date Completed</th>
                                    <th style={{ width: '120px' }}>Status</th>
                                    <th style={{ width: '70px' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {containmentActions.map((row, index) => (
                                    <tr key={`containment-${index}`}>
                                        <td><FormControl size="sm" value={row.actionTaken} onChange={(e) => this.updateContainmentRow(index, 'actionTaken', e.target.value)} /></td>
                                        <td><FormControl size="sm" value={row.responsiblePerson} onChange={(e) => this.updateContainmentRow(index, 'responsiblePerson', e.target.value)} /></td>
                                        <td><FormControl size="sm" type="date" value={row.dateCompleted} onChange={(e) => this.updateContainmentRow(index, 'dateCompleted', e.target.value)} /></td>
                                        <td>
                                            <FormControl as="select" size="sm" value={row.status} onChange={(e) => this.updateContainmentRow(index, 'status', e.target.value)}>
                                                {CAR_ROW_STATUS_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                                            </FormControl>
                                        </td>
                                        <td><Button variant="outline-danger" size="sm" onClick={() => this.removeContainmentRow(index)}>×</Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    <Button variant="outline-primary" size="sm" className="mb-3" onClick={this.addContainmentRow}>Add row</Button>

                    {this.renderSectionTitle('4. Root Cause Analysis (RCA)')}
                    <p className="text-muted small mb-2">
                        State the verified reason(s) why the problem happened (Occurrence Cause) and why it was missed by inspection (Detection Cause). Use the 5-Whys methodology below.
                    </p>
                    <FormGroup className="mb-3">
                        <Form.Label>Problem Statement</Form.Label>
                        <FormControl value={rootCauseAnalysis.problemStatement} onChange={(e) => this.updateRootCause('problemStatement', e.target.value)} />
                    </FormGroup>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <FormGroup key={`why-${n}`} className="mb-3">
                            <Form.Label>{n === 5 ? 'Why? (5) — Root cause established here' : `Why? (${n})`}</Form.Label>
                            <FormControl value={rootCauseAnalysis[`why${n}`]} onChange={(e) => this.updateRootCause(`why${n}`, e.target.value)} />
                        </FormGroup>
                    ))}
                    <FormGroup className="mb-3">
                        <Form.Label>Final Root Cause Statement</Form.Label>
                        <FormControl as="textarea" rows={2} value={rootCauseAnalysis.finalStatement} onChange={(e) => this.updateRootCause('finalStatement', e.target.value)} />
                    </FormGroup>

                    {this.renderSectionTitle('5. Permanent Corrective Action Plan')}
                    <p className="text-muted small mb-2">
                        What long-term changes will prevent this specific problem from ever happening again?
                    </p>
                    <div className="table-responsive mb-2">
                        <Table bordered size="sm" className="mb-0">
                            <thead>
                                <tr style={tableHeadStyle}>
                                    <th>Action Description</th>
                                    <th style={{ width: '160px' }}>System / Document Updated</th>
                                    <th style={{ width: '120px' }}>Owner</th>
                                    <th style={{ width: '130px' }}>Target Date</th>
                                    <th style={{ width: '130px' }}>Completion Date</th>
                                    <th style={{ width: '70px' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {correctiveActionPlan.map((row, index) => (
                                    <tr key={`plan-${index}`}>
                                        <td><FormControl size="sm" value={row.actionDescription} onChange={(e) => this.updatePlanRow(index, 'actionDescription', e.target.value)} /></td>
                                        <td><FormControl size="sm" value={row.systemDocumentUpdated} onChange={(e) => this.updatePlanRow(index, 'systemDocumentUpdated', e.target.value)} /></td>
                                        <td><FormControl size="sm" value={row.owner} onChange={(e) => this.updatePlanRow(index, 'owner', e.target.value)} /></td>
                                        <td><FormControl size="sm" type="date" value={row.targetDate} onChange={(e) => this.updatePlanRow(index, 'targetDate', e.target.value)} /></td>
                                        <td><FormControl size="sm" type="date" value={row.completionDate} onChange={(e) => this.updatePlanRow(index, 'completionDate', e.target.value)} /></td>
                                        <td><Button variant="outline-danger" size="sm" onClick={() => this.removePlanRow(index)}>×</Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    <Button variant="outline-primary" size="sm" className="mb-3" onClick={this.addPlanRow}>Add row</Button>

                    {this.renderSectionTitle('6. Effectiveness Verification')}
                    <p className="text-muted small mb-2">
                        To be filled out after a designated observation period (typically 30 to 90 days) to prove the fix worked.
                    </p>
                    <FormGroup className="mb-3">
                        <Form.Label>Verification method used</Form.Label>
                        <div>
                            {CAR_EFFECTIVENESS_METHODS.map((method) => (
                                <Form.Check
                                    key={method}
                                    type="checkbox"
                                    id={`eff-${method}`}
                                    label={method}
                                    checked={(effectivenessVerification.verificationMethods || []).includes(method)}
                                    onChange={() => this.toggleEffectivenessMethod(method)}
                                    inline
                                    className="me-3"
                                />
                            ))}
                        </div>
                    </FormGroup>
                    <FormGroup className="mb-3">
                        <Form.Label>Evidence checked</Form.Label>
                        <FormControl as="textarea" rows={2} value={effectivenessVerification.evidenceChecked} onChange={(e) => this.updateEffectiveness('evidenceChecked', e.target.value)} />
                    </FormGroup>
                    <FormGroup className="mb-3">
                        <Form.Label>Evidence photos</Form.Label>
                        <UploadFiles onUpload={this.uploadEvidencePhoto} />
                        {this.state.evidencePhotoMessage && (
                            <div className="small text-muted mt-1">{this.state.evidencePhotoMessage}</div>
                        )}
                        <ReportPhotoPreviewList
                            items={parseJsonArray(effectivenessVerification.evidencePhotos)}
                            onRemove={this.removeEvidencePhoto}
                        />
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Verified by</Form.Label></Col>
                        <Col md={9}><FormControl value={effectivenessVerification.verifiedBy} onChange={(e) => this.updateEffectiveness('verifiedBy', e.target.value)} placeholder="e.g. Quality Mgr" /></Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Verification date</Form.Label></Col>
                        <Col md={9}><FormControl type="date" value={formatDateOnly(effectivenessVerification.verificationDate)} onChange={(e) => this.updateEffectiveness('verificationDate', e.target.value)} /></Col>
                    </FormGroup>

                    {this.renderSectionTitle('7. Status — submit for approval')}
                    <p className="text-muted small mb-2">
                        Select status and submit. You can update progress each time the report is reviewed. Overdue is calculated on the summary when the target date has passed.
                    </p>
                    <FormGroup as={Row} className="mb-3 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Status</Form.Label></Col>
                        <Col md={9}>
                            <FormControl as="select" value={status} disabled={readOnly} onChange={(e) => this.handleStatusChange(e.target.value)}>
                                <option value="Open">Open</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Closed" disabled={isNew}>Closed</option>
                            </FormControl>
                        </Col>
                    </FormGroup>
                    <FormGroup as={Row} className="mb-4 align-items-center">
                        <Col md={3}><Form.Label className="mb-md-0">Date Closed</Form.Label></Col>
                        <Col md={9}>
                            <FormControl type="date" value={dateClosed} readOnly placeholder="Set automatically when status is Closed" />
                        </Col>
                    </FormGroup>

                    {showSave && (
                        <Button variant="primary" disabled={saving} onClick={this.handleSave}>
                            {isNew ? 'Create CAR' : 'Save CAR'}
                        </Button>
                    )}
                    {showSubmitApproval && (
                        <Button variant="success" disabled={saving} className="ms-2" onClick={this.handleSubmitForApproval}>
                            Submit for Approval
                        </Button>
                    )}
                    </fieldset>
                </Form>
                {/* Print source: clipped on screen but fully laid out for react-to-print clone */}
                <div aria-hidden style={{ position: 'fixed', left: '-200vw', top: 0, width: '794px', pointerEvents: 'none' }}>
                    <div ref={this.printRef} className="car-print-document" style={{ width: '794px', background: '#fff' }}>
                        <CarReportPrintView data={this.getPrintData()} />
                    </div>
                </div>
            </div>
        );
    }
}

function withRouter(Component) {
    return (props) => {
        const params = useParams();
        const navigate = useNavigate();
        return <Component {...props} params={params} navigate={navigate} />;
    };
}

const CorrectiveActionReportFormWithRouter = withRouter(CorrectiveActionReportForm);
export { CorrectiveActionReportFormWithRouter as CorrectiveActionReportForm };
